import * as T from 'three'
import type { SurfaceFaceStyle } from '../../../../../config/blissRoomFinish'

/** Decorative face in inches, local XY with its outward face along +Z.
 * No hardware: these are fascia/stringer faces, not drawer fronts. */
export function createFacePanel(outline: T.Vector2[], style: SurfaceFaceStyle, courseHeight = 6) {
  const root = new T.Group()
  root.name = `face-panel-${style}`
  function add(name: string, points: T.Vector2[], depth: number, offset = 0, shade = 1, hole?: T.Vector2[]) {
    const shape = new T.Shape(points)
    shape.closePath()
    if (hole) { const path = new T.Path(hole); path.closePath(); shape.holes.push(path) }
    const geometry = new T.ExtrudeGeometry(shape, {depth, bevelEnabled:false, steps:1})
    geometry.scale(1/12,1/12,1/12)
    const mesh = new T.Mesh(geometry,new T.MeshStandardMaterial({color:0xffffff,roughness:0.7}))
    mesh.name=name;mesh.position.z=offset/12
    mesh.userData.finishPart='frame';mesh.userData.finishShade=shade
    root.add(mesh)
  }
  if(style==='flat') { add('face-flat',outline,0.125);return root }
  add('face-recess',outline,0.01,0,style==='nickel_gap'?0.28:0.85)
  if(style==='shaker') {
    // Inset adjacent edges and intersect them for a continuous mitered border,
    // including the re-entrant corners of a notched stringer.
    const area=outline.reduce((sum,a,i)=>{const b=outline[(i+1)%outline.length];return sum+a.x*b.y-b.x*a.y},0)
    const direction=area>0?1:-1
    const ys=outline.map(p=>p.y), xs=outline.map(p=>p.x)
    const rail=Math.min(1.5,(Math.max(...ys)-Math.min(...ys))/4,(Math.max(...xs)-Math.min(...xs))/4)
    const inner=outline.map((p,i)=>{
      const previous=outline[(i+outline.length-1)%outline.length], next=outline[(i+1)%outline.length]
      const a=p.clone().sub(previous).normalize(), b=next.clone().sub(p).normalize()
      const na=new T.Vector2(-a.y,a.x).multiplyScalar(direction*rail), nb=new T.Vector2(-b.y,b.x).multiplyScalar(direction*rail)
      const pa=p.clone().add(na), pb=p.clone().add(nb), cross=a.x*b.y-a.y*b.x
      if(Math.abs(cross)<1e-8)return pa
      const delta=pb.sub(pa), along=(delta.x*b.y-delta.y*b.x)/cross
      return pa.addScaledVector(a,along)
    })
    add('face-shaker-frame',outline,0.125,0.01,1,inner)
    return root
  }
  function clip(points:T.Vector2[], y:number, above:boolean) {
    const result:T.Vector2[]=[]
    for(let i=0;i<points.length;i++){
      const a=points[i],b=points[(i+1)%points.length]
      const ai=above?a.y>=y:a.y<=y, bi=above?b.y>=y:b.y<=y
      if(ai)result.push(a)
      if(ai!==bi)result.push(new T.Vector2(a.x+(b.x-a.x)*(y-a.y)/(b.y-a.y),y))
    }
    return result
  }
  const bottom=Math.min(...outline.map(p=>p.y)), top=Math.max(...outline.map(p=>p.y))
  for(let y=bottom,i=0;y<top;y+=courseHeight,i++){
    const points=clip(clip(outline,y===bottom?bottom:y+0.0625,true),Math.min(top,y+courseHeight-0.0625),false)
    if(points.length>=3)add(`face-nickel-gap-course-${i}`,points,0.125,0.01)
  }
  return root
}

export function rectangularFace(width:number,height:number,style:SurfaceFaceStyle) {
  return createFacePanel([new T.Vector2(0,0),new T.Vector2(width,0),new T.Vector2(width,height),new T.Vector2(0,height)],style,Math.min(6,height/2))
}
